export const MAX_UPLOAD_BYTES = 25_000_000_000;
export const UPLOAD_TYPES = { mp3: 'audio/mpeg', mp4: 'video/mp4', wav: 'audio/wav' };
export const AUDIO_SHORTS_MESSAGE = 'Shorts requires an MP4 file. Transcription and show notes are available.';
export const UNKNOWN_SHORTS_MESSAGE = 'Only MP4, MP3, and WAV files are supported. Please choose a file in one of these formats.';

export function extension(name) {
  return typeof name === 'string' ? (name.match(/\.([^.\/\\]+)$/)?.[1] || '').toLowerCase() : '';
}

// Drive MIME metadata is independent of the user-editable filename.
export function mimeFormat(mimeType = '') {
  const mime = typeof mimeType === 'string' ? mimeType.toLowerCase().split(';')[0].trim() : '';
  const types = {
    mp3: ['audio/mpeg', 'audio/mp3', 'audio/x-mp3', 'audio/mpeg3', 'audio/x-mpeg-3'],
    mp4: ['video/mp4', 'audio/mp4', 'application/mp4'],
    wav: ['audio/wav', 'audio/x-wav', 'audio/wave', 'audio/vnd.wave'],
  };
  return Object.keys(types).find(format => types[format].includes(mime)) || '';
}

// Resolve a supported format. A recognized MIME wins. When the MIME is generic
// or absent (Drive reports application/octet-stream for some files), fall back
// to the filename extension — mirroring the trust the upload path already
// places in it. A present-but-unrecognized MIME (e.g. video/quicktime) resolves
// to nothing, so such files stay rejected rather than passing on their name.
export function resolveFormat(name, mimeType = '') {
  const byMime = mimeFormat(mimeType);
  if (byMime) return byMime;
  const mime = typeof mimeType === 'string' ? mimeType.toLowerCase().split(';')[0].trim() : '';
  if (mime && mime !== 'application/octet-stream') return '';
  const ext = extension(name);
  return Object.prototype.hasOwnProperty.call(UPLOAD_TYPES, ext) ? ext : '';
}

export function classifyMedia(name, mimeType = '') {
  const format = mimeFormat(mimeType);
  if (format) return format === 'mp4' ? 'video' : 'audio';
  const ext = extension(name);
  if (ext === 'mp4') return 'video';
  if (['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg', 'opus', 'aiff', 'aif', 'wma'].includes(ext)) return 'audio';
  if (['mov', 'mkv', 'avi', 'webm', 'm4v', 'wmv', 'mpeg', 'mpg', '3gp'].includes(ext)) return 'video';
  const mime = mimeType.toLowerCase().split(';')[0].trim();
  if (mime === 'audio/mp4' || mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'unknown';
}

export function uploadError(file) {
  if (!file) return 'Please select a file to upload.';
  const reason = formatError(file.name, file.type);
  if (reason) return reason;
  if (!Number.isSafeInteger(file.size) || file.size <= 0) return 'Please select a non-empty file with a valid size.';
  if (file.size > MAX_UPLOAD_BYTES) return 'This file exceeds the 25 GB upload limit.';
  return null;
}

export function uploadMimeType(file) {
  const error = uploadError(file);
  if (error) throw new Error(error);
  return UPLOAD_TYPES[extension(file.name)];
}

export function uploadInfo(file) {
  if (!file) return null;
  const mediaKind = classifyMedia(file.name, file.type);
  return { fileName: file.name, size: file.size, mimeType: file.type, mediaKind, shortsEligible: extension(file.name) === 'mp4' && !uploadError(file) };
}

export function runMediaKind(run) {
  const known = classifyMedia(run.originalFileName || '', run.sourceMimeType || '');
  if (known !== 'unknown') return known;
  const kind = (run.sourceMediaType !== 'unknown' && run.sourceMediaType) || run.mediaType;
  return kind === 'audio' || kind === 'video' ? kind : 'unknown';
}

export function formatFileSize(size) {
  if (!Number.isFinite(size)) return 'Size unavailable';
  if (size >= 1e9) return `${(size / 1e9).toFixed(2)} GB`;
  if (size >= 1e6) return `${(size / 1e6).toFixed(1)} MB`;
  if (size >= 1e3) return `${(size / 1e3).toFixed(1)} KB`;
  return `${size} bytes`;
}

// Browser MIME may be empty/generic; Drive metadata must identify the format.
export function formatError(name, mimeType = '', requireMime = false) {
  if (requireMime) {
    // MIME wins; generic/absent MIME falls back to the extension (see resolveFormat).
    return resolveFormat(name, mimeType) ? null : 'Only MP3, MP4, and WAV files are supported.';
  }
  const ext = extension(name);
  if (!Object.prototype.hasOwnProperty.call(UPLOAD_TYPES, ext)) return 'Only MP3, MP4, and WAV files are supported.';
  const mime = typeof mimeType === 'string' ? mimeType.toLowerCase().split(';')[0].trim() : '';
  if (!mime || mime === 'application/octet-stream') {
    return requireMime ? 'Cannot confirm this Drive file’s format. Use a file with a matching extension and media type.' : null;
  }
  const allowed = {
    mp3: ['audio/mpeg', 'audio/mp3', 'audio/x-mp3', 'audio/mpeg3', 'audio/x-mpeg-3'],
    mp4: ['video/mp4', 'audio/mp4', 'application/mp4'],
    wav: ['audio/wav', 'audio/x-wav', 'audio/wave', 'audio/vnd.wave'],
  };
  return allowed[ext].includes(mime) ? null : 'The filename extension and file type do not match. Select an MP3, MP4, or WAV file with matching metadata.';
}

export function driveFileError(file) {
  if (!file) return 'Could not confirm this Drive file’s metadata. Try checking it again.';
  const reason = formatError(file.fileName, file.mimeType, true);
  if (reason) return reason;
  if (!Number.isSafeInteger(file.size) || file.size <= 0) return 'Cannot confirm a non-empty Drive file. Check the file and try again.';
  return null;
}

export function runShortsEligible(run) {
  return resolveFormat(run.originalFileName, run.sourceMimeType) === 'mp4';
}
